/**
 * ContractorFieldLog
 * ------------------
 * The trade's daily log: headcount, hours, weather auto-pull, delays and
 * photos. They file it, the PM approves it. Approved logs are read-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Send, Camera, CloudSun, Trash2, ClipboardList, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PILL = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  revise_requested: "bg-red-50 text-red-700 ring-red-300",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function ContractorFieldLog({ token, projectId, projectName, tradeName }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/field-logs?project_id=${projectId}`, { headers });
      setRows(r.data?.field_logs || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const blank = () => ({
    log_date: today(), responsible_company: tradeName || "", trade: "",
    work_performed: "", areas_worked: "", toolbox_talk: "",
    safety_incident: false, safety_notes: "", ppe_compliant: true,
    crews: [{ company: tradeName || "", trade: "", workers: 1, hours: 8, notes: "" }],
    delays: [],
    weather: { conditions: "", temp_high: null, temp_low: null, precipitation_in: null, wind_mph: null, source: "manual" },
  });

  const pullWeather = async () => {
    setPulling(true);
    try {
      const r = await axios.get(
        `${API}/field-logs/weather?project_id=${projectId}&log_date=${form.log_date}`, { headers });
      setForm((f) => ({ ...f, weather: r.data.weather }));
      toast.success("Weather pulled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weather lookup failed");
    } finally { setPulling(false); }
  };

  const save = async (andSubmit) => {
    if (!form.work_performed.trim()) return toast.error("Describe the work performed");
    setBusy(true);
    try {
      let id = editingId;
      if (id) {
        await axios.put(`${API}/field-logs/${id}`, form, { headers });
      } else {
        const r = await axios.post(`${API}/field-logs`, { project_id: projectId, ...form }, { headers });
        id = r.data.field_log.id;
      }
      if (andSubmit) await axios.post(`${API}/field-logs/${id}/submit`, {}, { headers });
      toast.success(andSubmit ? "Daily log sent to the project manager" : "Daily log saved");
      setForm(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the log");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (row, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/field-logs/${row.id}/photos`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Photo added"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    }
  };

  const setCrew = (i, field, value) => setForm((f) => ({
    ...f, crews: f.crews.map((c, j) => (j === i ? { ...c, [field]: value } : c)),
  }));
  const setDelay = (i, field, value) => setForm((f) => ({
    ...f, delays: f.delays.map((d, j) => (j === i ? { ...d, [field]: value } : d)),
  }));

  if (!projectId) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="contractor-field-log">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">Daily field log</h3>
          <p className="text-xs text-slate-500">
            {projectName} — file your headcount, hours and photos each day. The PM approves it.
          </p>
        </div>
        {!form && (
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => { setEditingId(null); setForm(blank()); }}
            data-testid="contractor-new-field-log-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Today's log
          </Button>
        )}
      </div>

      {form && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/40 p-4 space-y-3 mb-4">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input type="date" value={form.log_date}
              onChange={(e) => setForm({ ...form, log_date: e.target.value })}
              data-testid="contractor-log-date-input" />
            <Input placeholder="Company" value={form.responsible_company}
              onChange={(e) => setForm({ ...form, responsible_company: e.target.value })} />
            <Input placeholder="Trade" value={form.trade}
              onChange={(e) => setForm({ ...form, trade: e.target.value })} />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Input className="max-w-[220px]" placeholder="Weather conditions"
              value={form.weather?.conditions || ""}
              onChange={(e) => setForm({ ...form, weather: { ...form.weather, conditions: e.target.value, source: "manual" } })} />
            <Button size="sm" variant="outline" onClick={pullWeather} disabled={pulling}
              data-testid="contractor-pull-weather-btn">
              {pulling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <CloudSun className="w-3.5 h-3.5 mr-1" />}Auto-pull
            </Button>
            {form.weather?.temp_high != null && (
              <span className="text-xs text-slate-600">
                {Math.round(form.weather.temp_high)}°/{Math.round(form.weather.temp_low)}°F ·{" "}
                {form.weather.precipitation_in}" rain
              </span>
            )}
          </div>

          <Textarea rows={3} placeholder="Work performed today"
            value={form.work_performed}
            onChange={(e) => setForm({ ...form, work_performed: e.target.value })}
            data-testid="contractor-log-work-input" />
          <Input placeholder="Areas worked" value={form.areas_worked}
            onChange={(e) => setForm({ ...form, areas_worked: e.target.value })} />

          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Manpower</p>
            {form.crews.map((c, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center">
                <Input className="sm:col-span-5" placeholder="Company" value={c.company}
                  onChange={(e) => setCrew(i, "company", e.target.value)} />
                <Input className="sm:col-span-3" placeholder="Trade" value={c.trade}
                  onChange={(e) => setCrew(i, "trade", e.target.value)} />
                <Input className="sm:col-span-2" type="number" placeholder="Workers" value={c.workers}
                  onChange={(e) => setCrew(i, "workers", Number(e.target.value))}
                  data-testid={`contractor-log-workers-${i}`} />
                <Input className="sm:col-span-2" type="number" step="0.5" placeholder="Hours" value={c.hours}
                  onChange={(e) => setCrew(i, "hours", Number(e.target.value))} />
              </div>
            ))}
            <Button size="sm" variant="outline"
              onClick={() => setForm({ ...form, crews: [...form.crews, { company: "", trade: "", workers: 1, hours: 8 }] })}>
              <Plus className="w-3 h-3 mr-1" />Add crew
            </Button>
          </div>

          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Delays</p>
            {form.delays.map((d, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center">
                <Input className="sm:col-span-8" placeholder="What held you up" value={d.description}
                  onChange={(e) => setDelay(i, "description", e.target.value)}
                  data-testid={`contractor-log-delay-${i}`} />
                <Input className="sm:col-span-3" type="number" step="0.5" placeholder="Hrs lost"
                  value={d.hours_lost}
                  onChange={(e) => setDelay(i, "hours_lost", Number(e.target.value))} />
                <Button size="icon" variant="ghost" className="sm:col-span-1"
                  onClick={() => setForm({ ...form, delays: form.delays.filter((_, j) => j !== i) })}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline"
              onClick={() => setForm({ ...form, delays: [...form.delays, { delay_type: "other", description: "", hours_lost: 0 }] })}>
              <Plus className="w-3 h-3 mr-1" />Add delay
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Input className="max-w-[240px]" placeholder="Toolbox talk topic" value={form.toolbox_talk}
              onChange={(e) => setForm({ ...form, toolbox_talk: e.target.value })} />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.safety_incident}
                onChange={(e) => setForm({ ...form, safety_incident: e.target.checked })}
                data-testid="contractor-log-incident-checkbox" />
              Safety incident today
            </label>
          </div>
          {form.safety_incident && (
            <Textarea rows={2} placeholder="What happened and what was done" value={form.safety_notes}
              onChange={(e) => setForm({ ...form, safety_notes: e.target.value })} />
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setForm(null); setEditingId(null); }}>Cancel</Button>
            <Button size="sm" variant="outline" onClick={() => save(false)} disabled={busy}>Save draft</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => save(true)} disabled={busy} data-testid="contractor-log-submit-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send to PM
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 rounded-lg border border-dashed border-slate-300">
          <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No daily logs filed yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="rounded-lg ring-1 ring-slate-200 p-3"
              data-testid={`contractor-log-row-${row.log_date}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {row.log_date} · {row.total_workers} workers · {row.total_manhours} man-hours
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 max-w-xl">{row.work_performed}</p>
                  {row.pm_notes ? (
                    <p className="text-xs text-red-700 mt-1 inline-flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5" />PM: {row.pm_notes}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${PILL[row.status]}`}>
                    {(row.status || "").replace(/_/g, " ")}
                  </span>
                  {["draft", "revise_requested"].includes(row.status) && (
                    <>
                      <Button size="sm" variant="ghost" className="text-slate-700"
                        onClick={() => { setEditingId(row.id); setForm({ ...blank(), ...row }); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-amber-700"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/field-logs/${row.id}/submit`, {}, { headers });
                            toast.success("Sent to the project manager"); load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
                        }}
                        data-testid={`contractor-log-send-${row.log_date}`}>
                        <Send className="w-3.5 h-3.5 mr-1" />Send
                      </Button>
                    </>
                  )}
                  {row.status !== "approved" && (
                    <label className="inline-flex items-center px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer"
                      title="Add photo" data-testid={`contractor-log-photo-${row.log_date}`}>
                      <Camera className="w-3.5 h-3.5" />
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(row, f); e.target.value = ""; }} />
                    </label>
                  )}
                  {row.status === "approved" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : null}
                </div>
              </div>
              {(row.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {row.photos.map((p) => (
                    <a key={p.id} href={`${BACKEND}${p.url}`} target="_blank" rel="noreferrer"
                      className="text-[11px] font-semibold bg-slate-100 ring-1 ring-slate-300 rounded-full px-2 py-1 hover:ring-amber-500">
                      {p.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
